import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {mergeAndAdd, PAYMENT_ACCOUNT_TYPE, ITokenizeResult} from '../../store';
import {ICreatePaymentAccount, IPaymentAccount} from './interfaces';
import {IBaseResponse, WebService} from '../../services';

interface ICreatePaymentAccountData extends ICreatePaymentAccount {
	cvv: string;
}

@Module({name: 'paymentAccount', namespaced: true})
class PaymentAccountModule extends VuexModule {
	paymentAccounts: IPaymentAccount[] = [];

	createPaymentAccountData: ICreatePaymentAccountData | null = null;

	@Action
	async fetchPaymentAccounts(locationId: string) {
		const response = await WebService.authenticatedClient.get<IBaseResponse<{paymentAccounts: IPaymentAccount[]}>>(`locations/${locationId}/payment_accounts`);
		this.setPaymentAccount(response.data.data.paymentAccounts);
	}

	get canAddPaymentAccount(): boolean {
		return !!this.createPaymentAccountData &&
			this.createPaymentAccountData.token !== '' &&
			this.createPaymentAccountData.name !== '' &&
			/\d\d\/\d\d/.test(this.createPaymentAccountData.expiration) &&
			/\d\d\d\d?/.test(this.createPaymentAccountData.cvv) &&
			this.createPaymentAccountData.captcha !== '';
	}

	@Action
	async createPaymentAccount() {
		if(!this.createPaymentAccountData) {
			throw new Error('No payment account data to create');
		}
		const body: ICreatePaymentAccount = {
			token: this.createPaymentAccountData.token,
			name: this.createPaymentAccountData.name,
			expiration: this.createPaymentAccountData.expiration,
			captcha: this.createPaymentAccountData.captcha
		};
		const response = await WebService.authenticatedClient.post<IBaseResponse<{paymentAccount: IPaymentAccount}>>('payment_accounts', {
			type: PAYMENT_ACCOUNT_TYPE.CARDCONNECT,
			...body,
			expiration: body.expiration.replace(/\//g, ''), // Remove the slash from the expiration date
		});
		this.addPaymentAccount(response.data.data.paymentAccount);

		const tokenizeResult: ITokenizeResult = {
			accountId: response.data.data.paymentAccount.accountId,
			cvv: this.createPaymentAccountData.cvv,
			signedToken: null,
		};
		this.clearCreatePaymentAccountData();
		return tokenizeResult;
	}

	@Action
	async deletePaymentAccount(paymentAccountId: string) {
		await WebService.authenticatedClient.post<IBaseResponse<{message: string}>>(`payment_accounts/${paymentAccountId}/delete`);
		this.removePaymentAccount(paymentAccountId);
	}

	@Mutation
	public setPaymentAccount(paymentAccounts: IPaymentAccount[]) {
		this.paymentAccounts = paymentAccounts;
	}

	@Mutation
	public addPaymentAccount(paymentAccount: IPaymentAccount | IPaymentAccount[]) {
		mergeAndAdd(this.paymentAccounts, paymentAccount, ['accountId']);
	}

	@Mutation
	public removePaymentAccount(paymentAccountId: string) {
		const index = this.paymentAccounts.findIndex((paymentAccount) => {
			return paymentAccount.accountId === paymentAccountId;
		});
		if(index !== -1) {
			this.paymentAccounts.splice(index, 1);
		}
	}

	@Mutation
	updateCreatePaymentAccountData(data: Partial<ICreatePaymentAccountData>) {
		this.createPaymentAccountData = {
			...(
				this.createPaymentAccountData || {
					name: '',
					token: '',
					expiration: '',
					captcha: '',
					cvv: '',
				}
			),
			...data
		};
	}

	@Mutation
	clearCreatePaymentAccountData() {
		this.createPaymentAccountData = null;
	}
}


export {
	PaymentAccountModule
};
